import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import { seccion } from "../components/layout/secciones";
import { confirmacionesLlaves, movimientosLlaves } from "../services/llaves";
import { useRealtime } from "../services/store";
import {
  afuera,
  confirmacionDe,
  demorada,
  estadoLlaves,
  ganchos,
  hace,
  rutaLlave,
} from "../shared/llaves";
import Loading from "./Loading";
import llaveImg from "../assets/llaves/llave_scania.webp";
import "./css/llaves.css";

/* ENTREGA DE LLAVES (ticket 522): quién tiene cada llave AHORA.
 *
 * Nico: «simplemente anotar no soluciona nada». Lo que faltaba no era otro
 * lugar donde anotar —eso ya existía en la barrera— sino la respuesta: qué
 * llaves están afuera, en manos de quién y desde cuándo. Eso es esta pantalla,
 * y lo que hace más tiempo que no vuelve va primero y resaltado.
 *
 * EL TABLERO (Igna, 27/10/2026: «un dash como el de las cajas de
 * herramientas pero de las llaves colgadas»): el panel de ganchos de la
 * portería, un gancho por tractor activo. La llave colgada es que está en
 * portería; el gancho vacío con su tarjeta, que está afuera y en manos de
 * quién; en rojo, la que pasó las 12 horas. Tocar un gancho abre la llave.
 * Abajo, la misma cuenta en lista, la más vieja primero.
 *
 * Los retiros y devoluciones los anotan los choferes por WhatsApp escaneando
 * el QR (en el bot, `llaves_wa.py`), o el hub en `/llaves/:interno`; las
 * etiquetas se imprimen desde acá.
 * Lo que se sigue anotando a mano en la barrera entra en la misma cuenta,
 * porque las dos cosas escriben en el mismo lugar. */

const horaCorta = (d) =>
  d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/* La llave que cuelga del gancho: la foto de una Scania con su argolla y su
 * chapita (Igna, 27/10: «en lugar de las llaves SVG, usá la imagen»). Sin
 * fondo, recortada y achicada a 300 px de alto (22 KB): la sombra la pone el
 * CSS, así se ve igual sobre la madera clara y la oscura. */
const LlaveDibujo = () => <img className="llv-dibujo" src={llaveImg} alt="" draggable="false" />;

const TableroLlaves = ({ lista, nombreDe, confirmaciones }) => (
  <div className="llv-tablero" data-testid="tablero-llaves">
    <ul className="llv-ganchos">
      {lista.map((g) => {
        const conf = confirmacionDe(g.est, confirmaciones);
        const tarde = g.afuera && (demorada(g.est) || Boolean(conf?.alerta));
        const quien = g.afuera ? nombreDe(g.est?.persona) : "";
        return (
          <li key={g.interno}>
            <Link
              to={rutaLlave(g.interno)}
              className={`llv-gancho${g.afuera ? " afuera" : " colgada"}${tarde ? " demorada" : ""}${
                conf ? ` sin-confirmar ${conf.tipo}` : ""
              }`}
              aria-label={
                g.afuera
                  ? `Llave del ${g.interno}: la tiene ${quien}, ${hace(g.est.desde)}${
                      conf ? `, ${conf.texto}` : ""
                    }`
                  : `Llave del ${g.interno}: en portería`
              }
              data-testid={`gancho-${g.interno}`}
            >
              <span className="llv-clavo" aria-hidden="true" />
              {g.afuera ? <span className="llv-vacio" aria-hidden="true" /> : <LlaveDibujo />}
              <span className="llv-chapa">
                <b>{g.interno}</b>
                {g.dominio && <small>{g.dominio}</small>}
              </span>
              {g.afuera && (
                <span className="llv-quien">
                  {quien}
                  <small>{hace(g.est.desde)}</small>
                  {conf && (
                    <small className="llv-conf" data-testid={`conf-${g.interno}`}>
                      {conf.texto}
                    </small>
                  )}
                </span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  </div>
);

const Llaves = () => {
  const { permisos = null } = useOutletContext() || {};
  const permiso = seccion("llaves")?.permiso || "ver.porteria";
  const puede = permisos === null || [].concat(permiso).some((p) => permisos.includes(p));
  const navigate = useNavigate();

  const personas = useRealtime("personas") || [];
  const tractores = useRealtime("tractores") || [];
  const activos = useRealtime("tractoresActivos");
  const [movs, setMovs] = useState(null);
  const [error, setError] = useState(false);
  const [buscar, setBuscar] = useState("");
  // Las confirmaciones de los choferes: si no se pueden leer, el tablero se
  // ve igual, sin las marcas.
  const [confirmaciones, setConfirmaciones] = useState([]);

  const traer = useCallback(async () => {
    try {
      setMovs(await movimientosLlaves());
      setError(false);
    } catch (e) {
      console.error("[Llaves] no se pudo consultar", e);
      setError(true);
    }
    try {
      setConfirmaciones(await confirmacionesLlaves());
    } catch (e) {
      console.error("[Llaves] no se pudieron leer las confirmaciones", e);
    }
  }, []);

  useEffect(() => {
    if (!puede) return undefined;
    traer();
    // Una llave que vuelve en la barrera tiene que dejar de figurar afuera sin
    // que el que mira toque nada.
    const t = setInterval(traer, 60000);
    return () => clearInterval(t);
  }, [traer, puede]);

  const estado = useMemo(() => (movs ? estadoLlaves(movs) : {}), [movs]);
  const lista = useMemo(() => afuera(estado), [estado]);
  const tablero = useMemo(() => ganchos(activos || [], estado), [activos, estado]);

  if (permisos !== null && !puede) return <PuertaCerrada />;
  if (error && !movs) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!movs) return <Loading />;

  const nombreDe = (dni) =>
    personas.find((p) => String(p.id) === String(dni))?.nombreCompleto || (dni ? `DNI ${dni}` : "—");
  const dominioDe = (interno) => tractores.find((t) => String(t.id) === interno)?.dominio || "";

  const abrir = (e) => {
    e.preventDefault();
    const v = buscar.trim();
    if (v) navigate(rutaLlave(v));
  };

  return (
    <div className="docs llaves">
      <div className="vig-novig-cab">
        <h3 className="docs-sub">
          En portería · {tablero.length - tablero.filter((g) => g.afuera).length} · Afuera ·{" "}
          {lista.length}
        </h3>
        <Link className="tk-chip" to="/llaves/etiquetas">
          Imprimir QR
        </Link>
      </div>

      {tablero.length > 0 && <TableroLlaves lista={tablero} nombreDe={nombreDe} confirmaciones={confirmaciones} />}

      <h3 className="docs-sub">Afuera ahora · {lista.length}</h3>

      {lista.length === 0 ? (
        <p className="panel-aviso suave">Todas las llaves están en portería.</p>
      ) : (
        <div className="tabla-wrap">
          <table className="tabla fija vig-tabla vig-cards llaves-tabla">
            <thead>
              <tr>
                <th>Llave</th>
                <th>La tiene</th>
                <th>Desde</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((l) => (
                <tr key={l.interno} className={demorada(l) ? "llave-demorada" : undefined}>
                  <td>
                    <Link to={rutaLlave(l.interno)}>
                      <b>{l.interno}</b>
                    </Link>
                    {dominioDe(l.interno) && <span className="sub">{dominioDe(l.interno)}</span>}
                  </td>
                  <td>
                    {nombreDe(l.persona)}
                    {confirmacionDe(l, confirmaciones) && (
                      <span className="sub llv-conf">{confirmacionDe(l, confirmaciones).texto}</span>
                    )}
                    {l.evento?.detalle && <span className="sub">{l.evento.detalle}</span>}
                  </td>
                  <td className="tenue">
                    {horaCorta(l.desde)}
                    <span className="sub">{hace(l.desde)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form className="llaves-buscar" onSubmit={abrir}>
        <input
          className="vig-filtro"
          type="search"
          inputMode="numeric"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder="Interno"
          aria-label="Abrir la llave de un interno"
          autoComplete="off"
        />
        <button type="submit" className="btn btn--secondary">
          Abrir
        </button>
      </form>
    </div>
  );
};

export default Llaves;
